import {
  Stack,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Page from "components/Layout/Page/Page";
import { useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { maskCpfCnpj } from "utils/Masks/Masks";
import { useHistory, useLocation } from "react-router-dom";
import PasswordCheckList from "components/Feedback/PasswordCheckList/PasswordCheckList";
import { useConsigazApi } from "hooks/useConsigazApi";
import PasswordImage from "assets/password.png";
import { toast } from "react-toastify";
import SuccessResetPasswordModal from "./SuccessResetPasswordModal";

export default function ResetPassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const resetToken = params.get("reset_token");

  const api = useConsigazApi();

  const handlePasswordFocus = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handlePasswordBlur = (e) => {
    formik.handleBlur(e);
    setAnchorEl(null);
  };

  const handleFormSubmit = async () => {
    const cleanCpfCnpj = formik.values.cpfCnpj.replace(/\D/g, "");
    try {
      const respSegment = await api.consultCpfCnpj(cleanCpfCnpj);
      const segment = respSegment.RetornoUsuario[0]["tipo-conta"] || null;
      if (!segment) {
        return toast.error("Segmento não encontrado.");
      }

      const resetResp = await api.resetPassword({
        operacao: "ResetSenha",
        origem: "app-site",
        cpfCnpj: cleanCpfCnpj,
        password: formik.values.password,
        type: segment,
        resetToken,
      });

      if (resetResp.Erros && resetResp.Erros.length) {
        return toast.error(resetResp.Erros[0].Descricao);
      }

      return setShowSuccessModal(true);
    } catch (err) {
      console.error("Erro ao mudar senha:", err);
      toast.error(err.message || err);
    }
  };

  const validationRegisterIndustrialSchema = Yup.object({
    cpfCnpj: Yup.string().required("CPF/CNPJ é obrigatório"),
    password: Yup.string()
      .required("Senha obrigatória")
      .min(8, "A senha deve ter pelo menos 8 caracteres")
      .matches(/[A-Z]/, "Deve conter ao menos uma letra maiúscula")
      .matches(/\d/, "Deve conter ao menos um número")
      .matches(/[!@#$%^&*]/, "Deve conter ao menos um caractere especial"),
    confirmPassword: Yup.string()
      .required("Confirmação de senha é obrigatória")
      .oneOf([Yup.ref("password")], "As senhas não coincidem"),
  });

  const formik = useFormik({
    initialValues: {
      cpfCnpj: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationRegisterIndustrialSchema,
    onSubmit: () => {
      handleFormSubmit();
    },
  });

  const router = useHistory();

  return (
    <Page>
      <Stack
        justifyContent={"center"}
        alignItems="center"
        height="100vh"
        direction={"row"}
      >
        <Stack
          border={"1px solid #fff"}
          padding={"32px 24px"}
          borderRadius={"8px"}
          direction={"row"}
          minHeight={"399px"}
          width={"930px"}
          gap={"40px"}
          mb={12}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Stack width={"80%"} mt={"20px"}>
            <TextField
              {...formik.getFieldProps("cpfCnpj")}
              variant="standard"
              placeholder="CPF/CNPJ"
              color="secondary"
              onChange={(e) => {
                const maskedCpfCnpj = maskCpfCnpj(e.target.value);
                formik.setFieldValue("cpfCnpj", maskedCpfCnpj);
              }}
              sx={{
                mt: "43px",
                "& .MuiInputBase-input": {
                  color: "#2CABF9",
                },
                "& input::placeholder": {
                  color: "gray",
                },
              }}
              error={formik.touched.cpfCnpj && Boolean(formik.errors.cpfCnpj)}
              helperText={formik.touched.cpfCnpj && formik.errors.cpfCnpj}
              fullWidth
            />
            <Stack direction={"row"} gap={"20px"} mt={"40px"}>
              <TextField
                variant="standard"
                placeholder="Senha"
                color="secondary"
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#2CABF9",
                  },
                  "& input::placeholder": {
                    color: "gray",
                  },
                  "& input": {
                    paddingLeft: "34px",
                  },
                }}
                type={showPassword ? "text" : "password"}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                {...formik.getFieldProps("password")}
                onFocus={handlePasswordFocus}
                onBlur={handlePasswordBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                fullWidth
              />
              <TextField
                variant="standard"
                placeholder="Confirmar senha"
                color="secondary"
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#2CABF9",
                  },
                  "& input::placeholder": {
                    color: "gray",
                  },
                  "& input": {
                    paddingLeft: "34px",
                  },
                }}
                type={showPassword ? "text" : "password"}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                {...formik.getFieldProps("confirmPassword")}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                fullWidth
              />
            </Stack>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                color: "#fff",
                height: "45px",
                borderRadius: "36px",
                marginTop: "55px",
                p: "10px",
              }}
              fullWidth
              onClick={formik.handleSubmit}
            >
              Avançar
            </Button>
          </Stack>
          <Stack justifyContent={"center"} alignItems={"center"} width={"30%"}>
            <Typography fontSize={"24px"} fontWeight={700} color="#fff">
              Redefinir senha
            </Typography>
            <Typography
              fontSize={"12px"}
              fontWeight={400}
              color="#fff"
              mb={"24px"}
              lineHeight={"100%"}
              mt={"8px"}
            >
              Informe, ao lado, seu CPF ou CNPJ e a nova senha a ser usada para
              acesso ao portal Consigaz.
            </Typography>
            <img
              src={PasswordImage}
              style={{
                height: "164px",
                width: "186px",
              }}
            />
          </Stack>
        </Stack>
      </Stack>
      <PasswordCheckList
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        value={formik.values.password}
      />
      <SuccessResetPasswordModal
        open={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          router.push("/login");
        }}
      />
    </Page>
  );
}
